import { useMemo, useState } from "react";
import { naturalSortKey } from "@/utils/naturalSort";
import type {
  SelfAllocationAllocable,
  SelfAllocationUnit,
} from "@/features/finances/types/selfAllocation";

interface Props {
  /** Allocable units — przedsięwzięcia albo grupy, zależnie od `mode`. */
  projects: SelfAllocationAllocable[];
  /** unit_id → fraction string (e.g. "0.4") */
  value: Record<string, string>;
  onChange: (next: Record<string, string>) => void;
  disabled?: boolean;
  /** Whether tiles are assigned to projects (default) or project groups. */
  mode?: SelfAllocationUnit;
}

const TILE_COUNT = 10;
const STEP = 0.1;

const TILE_COLORS = [
  "bg-amber-100 border-amber-400 text-amber-900",
  "bg-sky-100 border-sky-400 text-sky-900",
  "bg-emerald-100 border-emerald-400 text-emerald-900",
  "bg-rose-100 border-rose-400 text-rose-900",
  "bg-violet-100 border-violet-400 text-violet-900",
  "bg-orange-100 border-orange-400 text-orange-900",
  "bg-teal-100 border-teal-400 text-teal-900",
  "bg-fuchsia-100 border-fuchsia-400 text-fuchsia-900",
];

function pickColor(idx: number) {
  return TILE_COLORS[idx % TILE_COLORS.length];
}

/**
 * Convert fractions map → ordered list of project_id assigned to each of 10 tiles.
 *
 * Tile index 0 = "first 10%", ..., 9 = "last 10%". Sorting projects by stable
 * id keeps tile layout stable across re-renders.
 *
 * If the fraction map doesn't snap to multiples of STEP, the leftover percent
 * is left as empty tiles (so the user can see what's left to assign).
 */
function parseDecimal(s: string) {
  return parseFloat(s.replace(",", "."));
}

function fractionsToTiles(
  value: Record<string, string>,
  projects: SelfAllocationAllocable[],
): (string | null)[] {
  const tiles: (string | null)[] = Array(TILE_COUNT).fill(null);
  // Deterministic project order: by short_name.
  const sortedIds = projects
    .filter((p) => value[p.id] && parseDecimal(value[p.id]) > 0)
    .sort((a, b) => naturalSortKey(a.short_name).localeCompare(naturalSortKey(b.short_name)))
    .map((p) => p.id);

  let cursor = 0;
  for (const pid of sortedIds) {
    const frac = parseDecimal(value[pid] ?? "0");
    const slots = Math.round(frac * TILE_COUNT);
    for (let i = 0; i < slots && cursor < TILE_COUNT; i++) {
      tiles[cursor++] = pid;
    }
  }
  return tiles;
}

/** Rebuild value map from tiles. */
function tilesToFractions(tiles: (string | null)[]): Record<string, string> {
  const counts: Record<string, number> = {};
  for (const pid of tiles) {
    if (!pid) continue;
    counts[pid] = (counts[pid] ?? 0) + 1;
  }
  const out: Record<string, string> = {};
  for (const [pid, n] of Object.entries(counts)) {
    out[pid] = (n * STEP).toFixed(3);
  }
  return out;
}

export function SelfAllocationGridEditor({
  projects,
  value,
  onChange,
  disabled,
  mode = "project",
}: Props) {
  const isGroup = mode === "group";
  const L = {
    heading: isGroup ? "Grupy przedsięwzięć" : "Projekty",
    searchPlaceholder: isGroup
      ? "Szukaj po grupie lub kliencie…"
      : "Szukaj po projekcie lub kliencie…",
    activate: isGroup ? "grupę" : "projekt",
    activeChange: isGroup ? "aktywną grupę" : "aktywny projekt",
    emptyMatch: (q: string) =>
      isGroup
        ? `Brak grup pasujących do „${q}".`
        : `Brak projektów pasujących do „${q}".`,
  };
  const tiles = useMemo(
    () => fractionsToTiles(value, projects),
    [value, projects],
  );
  // Fractions whose unit isn't in `projects` (e.g. a przedsięwzięcie that has
  // since gone inactive, so it's dropped from the available list) can't be shown
  // on the 10×10% grid. Carry them through unchanged on every edit — otherwise
  // `tilesToFractions` rebuilds the map from tiles alone and silently drops an
  // allocation the user can't even see.
  const carryOver = useMemo(() => {
    const known = new Set(projects.map((p) => p.id));
    const out: Record<string, string> = {};
    for (const [id, frac] of Object.entries(value)) {
      if (!known.has(id)) out[id] = frac;
    }
    return out;
  }, [projects, value]);
  const [activeProject, setActiveProject] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  const filledCount = tiles.filter((t) => t !== null).length;
  const emptyCount = TILE_COUNT - filledCount;

  const projectColor = useMemo(() => {
    const sorted = [...projects].sort((a, b) =>
      a.short_name.localeCompare(b.short_name),
    );
    const map: Record<string, string> = {};
    sorted.forEach((p, i) => {
      map[p.id] = pickColor(i);
    });
    return map;
  }, [projects]);

  const fillsForProject = (pid: string) =>
    tiles.filter((t) => t === pid).length;

  // Filter visible projects by short_name, project name, or client name.
  // Filled projects always stay visible (so the user sees what's in the grid),
  // and the active project as well — otherwise the search could surprise.
  const filteredProjects = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return projects;
    return projects.filter((p) => {
      if (p.id === activeProject) return true;
      if (fillsForProject(p.id) > 0) return true;
      return (
        p.short_name.toLowerCase().includes(q) ||
        p.name.toLowerCase().includes(q) ||
        (p.client_name ?? "").toLowerCase().includes(q)
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects, search, activeProject, tiles]);

  const handleTileClick = (i: number) => {
    if (disabled) return;
    const current = tiles[i];
    if (current) {
      // Click on a filled tile: select that project as active OR if it's already active, remove this tile.
      if (current === activeProject) {
        const next = [...tiles];
        next[i] = null;
        onChange({ ...carryOver, ...tilesToFractions(next) });
      } else {
        setActiveProject(current);
      }
    } else {
      // Click on empty tile: assign active project.
      if (!activeProject) return;
      const next = [...tiles];
      next[i] = activeProject;
      onChange({ ...carryOver, ...tilesToFractions(next) });
    }
  };

  const handleProjectPick = (pid: string) => {
    if (disabled) return;
    setActiveProject((cur) => (cur === pid ? null : pid));
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">
        Każdy kafelek = <strong>10%</strong> czasu pracy. Kliknij {L.activate} z
        listy poniżej, aby ją aktywować, a potem kliknij puste kafelki, aby
        przypisać. Kliknij wypełniony kafelek, aby zmienić {L.activeChange};
        kliknij ponownie, aby usunąć.
      </p>

      <div
        className="grid grid-cols-5 sm:grid-cols-10 gap-2"
        data-testid="grid-tiles"
      >
        {tiles.map((pid, i) => {
          const proj = pid ? projects.find((p) => p.id === pid) : null;
          const klass = pid
            ? (projectColor[pid] ?? "bg-gray-100 border-gray-300 text-gray-700")
            : "bg-white border-dashed border-gray-300 text-gray-400";
          const isActive = pid && pid === activeProject;
          return (
            <button
              key={i}
              type="button"
              disabled={disabled}
              onClick={() => handleTileClick(i)}
              data-testid={`tile-${i}`}
              data-project={pid ?? ""}
              className={`h-16 rounded border-2 text-xs font-mono flex flex-col items-center justify-center transition ${klass} ${
                isActive ? "ring-2 ring-blue-500 ring-offset-1" : ""
              } ${disabled ? "cursor-default" : "cursor-pointer hover:opacity-80"}`}
              title={proj ? `${proj.short_name} — ${proj.name}` : "Puste"}
            >
              <span className="text-[10px] uppercase tracking-widest opacity-70">
                {(i + 1) * 10}%
              </span>
              <span className="text-sm font-semibold">
                {proj?.short_name ?? "—"}
              </span>
            </button>
          );
        })}
      </div>

      <div className="text-xs text-gray-500" data-testid="grid-summary">
        Wypełnione: <strong>{filledCount * 10}%</strong> · Pozostało:{" "}
        <strong
          className={emptyCount === 0 ? "text-green-700" : "text-amber-700"}
        >
          {emptyCount * 10}%
        </strong>
      </div>

      <div className="border-t border-gray-200 pt-3">
        <div className="flex items-center justify-between mb-2 gap-3">
          <p className="text-xs uppercase tracking-widest text-gray-500">
            {L.heading}
          </p>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={L.searchPlaceholder}
            data-testid="grid-projects-search"
            className="text-sm border border-gray-300 rounded px-2 py-1 w-64"
          />
        </div>
        {filteredProjects.length === 0 && (
          <p className="text-xs text-gray-400 py-2">
            {L.emptyMatch(search)}
          </p>
        )}
        <ul className="space-y-1" data-testid="grid-projects">
          {filteredProjects.map((p) => {
            const fills = fillsForProject(p.id);
            const isActive = activeProject === p.id;
            const dotKlass = projectColor[p.id] ?? "bg-gray-100";
            return (
              <li key={p.id}>
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => handleProjectPick(p.id)}
                  data-testid={`grid-project-${p.short_name}`}
                  className={`w-full flex items-center justify-between gap-3 text-sm px-2 py-1 rounded ${
                    isActive
                      ? "bg-blue-50 ring-1 ring-blue-300"
                      : "hover:bg-gray-50"
                  } ${disabled ? "cursor-default" : "cursor-pointer"}`}
                >
                  <span className="flex items-center gap-2 min-w-0">
                    <span
                      className={`inline-block w-3 h-3 rounded-sm border ${dotKlass}`}
                    />
                    <span className="font-mono text-xs text-gray-500 shrink-0">
                      {p.short_name}
                    </span>
                    <span className="truncate text-gray-700">
                      {p.name}
                      {p.client_name && (
                        <span className="text-xs text-gray-500 ml-2">
                          — {p.client_name}
                        </span>
                      )}
                    </span>
                  </span>
                  <span className="shrink-0 font-mono text-xs">
                    {fills > 0 ? `${fills * 10}%` : "—"}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

/**
 * Detect whether a set of fractions can be cleanly represented in the 10×10% grid.
 * True iff every fraction × 10 is close to an integer (within ±0.001 tolerance).
 */
export function fractionsAreGridFriendly(
  fractions: Record<string, string> | Array<{ fraction: string }>,
): boolean {
  const values = Array.isArray(fractions)
    ? fractions.map((f) => parseDecimal(f.fraction))
    : Object.values(fractions).map((v) => parseDecimal(v));
  if (values.length === 0) return false;
  return values.every((v) => {
    const scaled = v * 10;
    return Math.abs(scaled - Math.round(scaled)) <= 0.01;
  });
}
